/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import {
  FunctionDeclaration,
  LiveServerToolCall,
  Modality,
  Type,
} from "@google/genai";

/** 1. Chính sách Vietnam Airlines – GET */
const POLICY_WEBHOOK_URL =
  process.env.REACT_APP_VNA_POLICY_WEBHOOK_URL ||
  "https://hai1975.app.n8n.cloud/webhook/vna_policy";

/** 2. Tìm chuyến bay – POST body { fromAirportCode, toAirportCode, departDate } */
const SEARCH_FLIGHT_WEBHOOK_URL =
  process.env.REACT_APP_VNA_SEARCH_FLIGHT_WEBHOOK_URL ||
  "https://hai1975.app.n8n.cloud/webhook/vna_search_flight";

/** 3. Danh sách booking – GET, filter theo phone hoặc booking_number */
const BOOKING_GET_ALL_URL =
  process.env.REACT_APP_VNA_BOOKING_GET_ALL_URL ||
  "https://hai1975.app.n8n.cloud/webhook/vna_booking_getAll";

const vietnamAirlinesPolicyDeclaration: FunctionDeclaration = {
  name: "vietnam_airlines_policy",
  description:
    "Lấy thông tin chính sách Vietnam Airlines theo kịch bản A-B-C-D: hành lý, đổi vé/thay đổi vé, dịch vụ người khuyết tật (xe lăn, chăm sóc đặc biệt), hoàn tiền, no-show, check-in, giá vé. Gọi khi hành khách hỏi về các chủ đề trên. Kết quả (policy_info) có thể chứa kịch bản A-B-C-D; bot phải trả lời đúng theo từng bước trong đó. Truyền policy_topic: 'baggage' (hành lý), 'check-in' (làm thủ tục), hoặc để trống cho chủ đề khác.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      policy_topic: {
        type: Type.STRING,
        description:
          "Chủ đề: 'baggage' (hành lý, xách tay, ký gửi), 'check-in' (làm thủ tục), hoặc trống (đổi vé, dịch vụ người khuyết tật/xe lăn/chăm sóc, hoặc chung).",
      },
    },
    required: [],
  },
};

const vnaSearchFlightDeclaration: FunctionDeclaration = {
  name: "vna_search_flight",
  description:
    "Tìm chuyến bay Vietnam Airlines theo sân bay đi, sân bay đến và ngày bay. Ưu tiên chuyến bay do Vietnam Airlines khai thác. Gọi khi người dùng hỏi tìm chuyến bay, lịch bay, giá vé (cần mã sân bay và ngày). Ví dụ: SGN=TP.HCM, HAN=Hà Nội, DAD=Đà Nẵng, PQC=Phú Quốc.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      fromAirportCode: {
        type: Type.STRING,
        description:
          "Mã sân bay đi (IATA), ví dụ: SGN, HAN, DAD, PQC, CXR, VCS.",
      },
      toAirportCode: {
        type: Type.STRING,
        description:
          "Mã sân bay đến (IATA), ví dụ: SGN, HAN, DAD, PQC, CXR, VCS.",
      },
      departDate: {
        type: Type.STRING,
        description:
          "Ngày khởi hành định dạng YYYY-MM-DD, ví dụ: 2026-01-29.",
      },
    },
    required: ["fromAirportCode", "toAirportCode", "departDate"],
  },
};

const findBookingDeclaration: FunctionDeclaration = {
  name: "find_booking",
  description:
    "Tìm thông tin đặt chỗ/khách hàng theo số điện thoại hoặc mã đặt chỗ (booking code). Gọi khi khách hàng muốn tra cứu đặt chỗ, xác nhận vé, tìm booking theo SĐT hoặc mã đặt chỗ.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      phone: {
        type: Type.STRING,
        description: "Số điện thoại khách hàng (có thể có hoặc không dấu cách).",
      },
      booking_code: {
        type: Type.STRING,
        description: "Mã đặt chỗ (booking number), ví dụ: ABCDEF, 8HGLGF.",
      },
    },
    required: [],
  },
};

/** 4. Sơ đồ sân bay – trả về URL bản đồ sân bay theo mã IATA */
const AIRPORT_MAP_BASE = "https://www.airportinformation.com";

const findAirportMapDeclaration: FunctionDeclaration = {
  name: "find_airport_map",
  description:
    "Hiển thị sơ đồ/bản đồ sân bay khi khách hỏi xem sơ đồ sân bay, bản đồ sân bay, map sân bay. Cần mã sân bay IATA (3 chữ cái), ví dụ: AMS=Amsterdam, SGN=TP.HCM, HAN=Hà Nội, DAD=Đà Nẵng.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      airportCode: {
        type: Type.STRING,
        description:
          "Mã sân bay IATA 3 chữ cái, ví dụ: AMS, SGN, HAN, DAD, PQC, LHR, CDG.",
      },
    },
    required: ["airportCode"],
  },
};

/** Fallback khi webhook chính sách lỗi */
const POLICY_FALLBACK =
  "Hiện không tải được chính sách. Bạn vui lòng thử lại hoặc xem tại vietnamairlines.com.";

/** Fallback khi webhook tìm chuyến bay lỗi */
const FLIGHT_SEARCH_FALLBACK =
  "Hiện không tra cứu được chuyến bay. Bạn vui lòng thử lại sau hoặc tra cứu trên vietnamairlines.com.";

/** Fallback khi tra booking lỗi */
const FIND_BOOKING_FALLBACK =
  "Hiện không tra cứu được thông tin đặt chỗ. Bạn vui lòng thử lại hoặc cung cấp số điện thoại / mã đặt chỗ.";

/** Lấy chuỗi từ response (n8n/API trả content, data, output, text, ...) */
function getTextFromResponse(data: unknown): string {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof data === "object" && data !== null) {
    const o = data as Record<string, unknown>;
    const key = [
      "data",
      "content",
      "output",
      "text",
      "policy_info",
      "flight_info",
      "message",
      "reply",
      "result",
      "chatOutput",
    ].find((k) => typeof o[k] === "string");
    if (key) return o[key] as string;
  }
  return "";
}

function AltairComponent() {
  const { client, config, setConfig, setModel, setAirportMapUrl, setShowBookingInput } =
    useLiveAPIContext();

  useEffect(() => {
    setModel("models/gemini-2.5-flash-native-audio-preview-12-2025");
    setConfig({
      ...config,
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        ...config.speechConfig,
        languageCode: "vi-VN",
        voiceConfig: config.speechConfig?.voiceConfig ?? {
          prebuiltVoiceConfig: { voiceName: "Puck" },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: 'Bạn là trợ lý AI. Bạn có thể nói nhiều thứ tiếng (tiếng Việt, Anh, Hà Lan, Pháp, Trung Quốc, Nhật, Ả Rập, Thái, và các ngôn ngữ khác). PHẢN HỒI BẰNG NGÔN NGỮ CÙNG VỚI CÂU HỎI của khách. QUAN TRỌNG - PHẠM VI DỊCH VỤ: Chỉ trả lời các câu hỏi liên quan đến Vietnam Airlines. Tìm chuyến bay chỉ dành cho chuyến bay của Vietnam Airlines. Nếu hành khách hỏi về chuyến bay của hãng hàng không khác (Bamboo, Vietjet, Vietravel, Jetstar, v.v.), bạn hãy vui vẻ trả lời: "Chúng tôi chỉ cung cấp được thông tin chuyến bay của Vietnam Airlines." QUAN TRỌNG: Khi khách hàng gửi tin nhắn "Bắt đầu" (hoặc đây là tin nhắn đầu tiên trong phiên), bạn PHẢI trả lời ĐÚNG CÂU NÀY bằng giọng nói, không thêm bớt: "Hãng hàng không quốc gia Việt Nam kính chào quý khách, tôi là nhân viên dịch vụ chăm sóc khách hàng, bạn có thể cho mình biết tên và yêu cầu của bạn không ?" Sau đó mới xử lý các câu hỏi khác. Dùng đúng hàm: (1) Chính sách → gọi "vietnam_airlines_policy". Nếu khách hỏi về hành lý (xách tay, ký gửi) thì truyền policy_topic: "baggage". Nếu hỏi về làm thủ tục/check-in thì truyền policy_topic: "check-in". Chủ đề khác thì không truyền policy_topic. KỊCH BẢN A-B-C-D (BẮT BUỘC): Khi hành khách hỏi về dịch vụ người khuyết tật (xe lăn, chăm sóc đặc biệt), hành lý, hoặc thay đổi vé/đổi vé, bạn PHẢI gọi "vietnam_airlines_policy" với policy_topic phù hợp và trả lời ĐÚNG THEO kịch bản A-B-C-D đã được hướng dẫn trong nội dung policy_info do tool trả về: trả lời lần lượt theo từng bước A, B, C, D trong đó; không bỏ bước, không thêm nội dung ngoài kịch bản. (2) Tìm chuyến bay (theo sân bay đi/đến và ngày) → gọi "vna_search_flight" với fromAirportCode, toAirportCode, departDate (YYYY-MM-DD). Mã sân bay: SGN=TP.HCM, HAN=Hà Nội, DAD=Đà Nẵng, PQC=Phú Quốc, CXR=Nha Trang, VCS=Côn Đảo. Khi khách hỏi tìm chuyến bay, ưu tiên tìm chuyến bay do Vietnam Airlines khai thác. (3) Tra cứu đặt chỗ theo số điện thoại hoặc mã đặt chỗ (booking code) → gọi "find_booking" với phone và/hoặc booking_code (ít nhất một trong hai). (4) Khi khách hỏi xem sơ đồ sân bay, bản đồ sân bay → gọi "find_airport_map" với airportCode (mã IATA 3 chữ cái). Sau khi nhận kết quả (policy_info, flight_info, booking_info hoặc airport_map_url), chỉ đọc lại hoặc tóm tắt đúng nội dung đó. KẾT THÚC CUỘC GỌI: Khi khách hàng cho biết không còn câu hỏi (ví dụ: tạm biệt, cảm ơn, vậy thôi, không còn gì), bạn hãy trả lời ngắn gọn, lịch sự và văn minh. Quan trọng: câu chào kết thúc (tạm biệt và thông báo kết thúc cuộc gọi) PHẢI theo đúng ngôn ngữ mà khách đang nói: khách nói tiếng Việt thì chào tạm biệt bằng tiếng Việt (ví dụ "Cảm ơn quý khách đã liên hệ. Chúc quý khách một ngày tốt lành, xin chào tạm biệt. Nếu quý khách không còn yêu cầu gì thêm, cuộc gọi sẽ được kết thúc. Kính chào quý khách."); khách nói tiếng Anh hoặc ngôn ngữ khác thì chào tạm biệt bằng ngôn ngữ đó. Rồi kết thúc lượt trả lời.',
          },
        ],
      },
      tools: [
        {
          functionDeclarations: [
            vietnamAirlinesPolicyDeclaration,
            vnaSearchFlightDeclaration,
            findBookingDeclaration,
            findAirportMapDeclaration,
          ],
        },
      ],
    });
  }, [setConfig, setModel]);

  useEffect(() => {
    const onToolCall = async (toolCall: LiveServerToolCall) => {
      const functionCalls = toolCall.functionCalls;
      if (!functionCalls?.length) return;

      const results = await Promise.all(
        functionCalls.map(async (fc) => {
          let resultText: string;

          if (fc.name === vietnamAirlinesPolicyDeclaration.name) {
            setAirportMapUrl(null);
            setShowBookingInput(false);
            try {
              const res = await fetch(POLICY_WEBHOOK_URL, { method: "GET" });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const data: unknown = await res.json().catch(() => ({}));
              resultText = getTextFromResponse(data);
              if (!resultText.trim()) resultText = POLICY_FALLBACK;
            } catch (err) {
              console.warn("Policy webhook failed:", err);
              resultText = POLICY_FALLBACK;
            }
            return {
              id: fc.id,
              name: fc.name,
              response: { output: { policy_info: resultText } },
            };
          }

          if (fc.name === vnaSearchFlightDeclaration.name) {
            setShowBookingInput(false);
            const args = (fc.args || {}) as {
              fromAirportCode?: string;
              toAirportCode?: string;
              departDate?: string;
            };
            const fromAirportCode = String(args.fromAirportCode ?? "").trim();
            const toAirportCode = String(args.toAirportCode ?? "").trim();
            const departDate = String(args.departDate ?? "").trim();

            try {
              const res = await fetch(SEARCH_FLIGHT_WEBHOOK_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  fromAirportCode,
                  toAirportCode,
                  departDate,
                }),
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const data: unknown = await res.json().catch(() => ({}));
              resultText = getTextFromResponse(data);
              if (!resultText.trim()) resultText = FLIGHT_SEARCH_FALLBACK;
            } catch (err) {
              console.warn("Search flight webhook failed:", err);
              resultText = FLIGHT_SEARCH_FALLBACK;
            }
            return {
              id: fc.id,
              name: fc.name,
              response: { output: { flight_info: resultText } },
            };
          }

          if (fc.name === findBookingDeclaration.name) {
            setShowBookingInput(true);
            const args = (fc.args || {}) as {
              phone?: string;
              booking_code?: string;
            };
            const phone = String(args.phone ?? "").trim().replace(/\s/g, "");
            const bookingCode = String(args.booking_code ?? "").trim().toUpperCase();

            try {
              const res = await fetch(BOOKING_GET_ALL_URL, { method: "GET" });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const data: unknown = await res.json().catch(() => []);
              const list = Array.isArray(data) ? data : [];
              type BookingRow = {
                Customer_name?: string;
                booking_number?: string;
                phone?: string;
                email?: string;
                more_information?: string;
                id?: number;
              };
              const matches = (list as BookingRow[]).filter((row) => {
                const rowPhone = String(row.phone ?? "").replace(/\s/g, "");
                const rowCode = String(row.booking_number ?? "").trim().toUpperCase();
                if (phone && rowPhone && rowPhone.includes(phone)) return true;
                if (phone && rowPhone === phone) return true;
                if (bookingCode && rowCode === bookingCode) return true;
                return false;
              });
              if (matches.length === 0) {
                resultText = "Không tìm thấy đặt chỗ nào phù hợp với thông tin bạn cung cấp.";
              } else {
                resultText = matches
                  .map((r) => {
                    const base = `Khách hàng: ${r.Customer_name ?? "—"}; Mã đặt chỗ: ${r.booking_number ?? "—"}; SĐT: ${r.phone ?? "—"}; Email: ${r.email ?? "—"}`;
                    const more = (r.more_information ?? "").trim();
                    return more ? `${base}; Thông tin thêm: ${more}.` : `${base}.`;
                  })
                  .join("\n");
              }
            } catch (err) {
              console.warn("Find booking webhook failed:", err);
              resultText = FIND_BOOKING_FALLBACK;
            }
            return {
              id: fc.id,
              name: fc.name,
              response: { output: { booking_info: resultText } },
            };
          }

          if (fc.name === findAirportMapDeclaration.name) {
            setShowBookingInput(false);
            const args = (fc.args || {}) as { airportCode?: string };
            const code = String(args.airportCode ?? "").trim().toUpperCase();
            const url =
              code.length >= 2
                ? `${AIRPORT_MAP_BASE}/${code}/map#destinationmap`
                : "";
            if (url) setAirportMapUrl(url);
            const message = url
              ? "Tôi sẽ cho bạn xem sơ đồ sân bay."
              : "Vui lòng cung cấp mã sân bay IATA (3 chữ cái), ví dụ: AMS, SGN, HAN.";
            return {
              id: fc.id,
              name: fc.name,
              response: { output: { airport_map_url: url || message, message } },
            };
          }

          resultText = "Hàm không được hỗ trợ.";
          return {
            id: fc.id,
            name: fc.name,
            response: { output: { policy_info: resultText } },
          };
        })
      );

      client.sendToolResponse({ functionResponses: results });
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, setAirportMapUrl, setShowBookingInput]);

  return <div className="vega-embed" />;
}

export const Altair = memo(AltairComponent);
